/**
 * Escapes HTML for security
 */
export const escapeHtml = (text: string): string => {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

/**
 * Extracts plain text from contentEditable element
 * Maintaining newlines for block elements
 */
export const extractPlainText = (element: HTMLElement): string => {
  const walker = document.createTreeWalker(
    element,
    NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
    null,
  );
  let text = '';
  let node: Node | null;
  let prevNode: Node | null = null;

  while ((node = walker.nextNode())) {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent || '';
      prevNode = node;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;

      // Handle line breaks
      if (el.tagName === 'BR') {
        text += '\n';
        prevNode = node;
      }
      // For block elements, add newline before them (except first child)
      else if (['DIV', 'P'].includes(el.tagName) && prevNode !== null) {
        // Only add newline if there's content before this block
        if (text.length > 0 && !text.endsWith('\n')) {
          text += '\n';
        }
      }
    }
  }

  return text;
};

/**
 * Checks if a macro is valid by checking if it can be replaced with a real value
 * A macro is valid if:
 * 1. It's a system macro (now, now+1, etc.) - these can always be replaced
 * 2. It exists in the suggestions list (parent dataset, lookup, or stage macros)
 *
 * @param macroText - The full macro text including braces (e.g., "{path_params.customerID}")
 * @param suggestions - List of available macro suggestions
 * @returns True if the macro can be replaced with a real value
 */
export const isValidMacro = (macroText: string, suggestions: Array<{ name: string }>): boolean => {
  // System macros (now, now+1, now-5, now-1000, etc.) are always valid
  // Remove braces to get the content
  const content = macroText.slice(1, -1);

  // Check if it's a system macro: now, now+1, now-5, now-1000, etc.
  // Pattern: starts with "now", optionally followed by + or - and one or more digits
  const isSystemMacro = /^now([+-]\d+)?$/.test(content);
  if (isSystemMacro) {
    return true;
  }

  // Check if this macro exists in the suggestions list
  // The suggestions list contains all available macros from parent dataset, lookups, and stages
  return suggestions.some((suggestion) => suggestion.name === macroText);
};

/**
 * Find all macros matching the pattern and highlight them
 * @param text - The text to parse
 * @param suggestions - List of available macro suggestions to validate against
 */
export const parseAndRenderContent = (
  text: string,
  suggestions: Array<{ name: string }> = [],
): string => {
  if (!text) return '';

  const MACRO_REGEX = /\{[a-zA-Z0-9._+\-*/=]+\}/g;

  const parts: string[] = [];
  let lastIndex = 0;

  // Find all macro matches using matchAll
  const matches = Array.from(text.matchAll(MACRO_REGEX));

  for (const match of matches) {
    const matchText = match[0];
    const matchIndex = match.index!;

    // Add text before this match
    if (matchIndex > lastIndex) {
      parts.push(escapeHtml(text.slice(lastIndex, matchIndex)));
    }

    // Check if this macro is valid (can be replaced with a real value)
    const valid = isValidMacro(matchText, suggestions);
    const macroClass = valid ? 'macro macro-valid' : 'macro macro-invalid';

    // Replace each macro match with the styled clickable macro component
    parts.push(
      `<span class="${macroClass}" data-macro="${escapeHtml(matchText)}" data-valid="${valid}" spellcheck="false">${escapeHtml(matchText)}</span>`,
    );

    lastIndex = matchIndex + matchText.length;
  }

  // Add remaining text after last match
  if (lastIndex < text.length) {
    parts.push(escapeHtml(text.slice(lastIndex)));
  }

  return parts.join('').replace(/\n/g, '<br>');
};

/**
 * Gets cursor position as character offset
 */
export const getCursorPosition = (element: HTMLElement): number | null => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;

  const range = selection.getRangeAt(0);
  const startContainer = range.startContainer;
  const startOffset = range.startOffset;

  let acc = 0;
  let prevNode: Node | null = null;

  const walker = document.createTreeWalker(
    element,
    NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
    null,
  );

  let node: Node | null;

  while ((node = walker.nextNode())) {
    if (node.nodeType === Node.TEXT_NODE) {
      if (node === startContainer) {
        return acc + Math.min(startOffset, node.textContent?.length ?? 0);
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      // If cursor is in an element node (not a text node), return current position
      if (el.contains(startContainer) && startContainer.nodeType !== Node.TEXT_NODE) {
        return acc;
      }
    }

    const len = getNodeTextLength(node, acc, prevNode);
    acc += len;
    prevNode = node;
  }

  return acc;
};

/**
 * Find the nearest open brace '{' before the caret and return absolute offsets:
 * { openIndex, cursorIndex } where cursorIndex is current caret absolute position.
 * Returns null if not typing inside a macro.
 */
export const findMacroRangeAtCursor = (
  editor: HTMLElement,
): { openIndex: number; cursorIndex: number } | null => {
  const cursorIndex = getCursorPosition(editor);
  if (cursorIndex === null) return null;

  const fullText = extractPlainText(editor);
  if (!fullText) return null;

  let openIndex = -1;
  for (let i = cursorIndex - 1; i >= 0; i--) {
    if (fullText[i] === '{') {
      openIndex = i;
      break;
    }
    // a closing brace before an opening => not inside macro
    if (fullText[i] === '}') {
      break;
    }
  }

  if (openIndex === -1) return null;
  return { openIndex, cursorIndex };
};

/**
 * Calculate the text length contribution of a node for offset calculation.
 * This matches the logic used in extractPlainText to ensure offsets align correctly.
 */
const getNodeTextLength = (node: Node, acc: number, prevNode: Node | null): number => {
  if (node.nodeType === Node.TEXT_NODE) {
    // Text nodes are always counted (including those inside macro spans)
    return node.textContent?.length || 0;
  }

  if (node.nodeType === Node.ELEMENT_NODE) {
    const el = node as HTMLElement;
    if (el.tagName === 'BR') {
      return 1;
    }
    if (['DIV', 'P'].includes(el.tagName)) {
      // Block elements add newline before them (except first child)
      return acc > 0 && prevNode !== null ? 1 : 0;
    }
    // Macro spans don't contribute to offset - their text nodes are counted separately
    // (extractPlainText doesn't count macro spans, only their text node children)
    if (el.classList.contains('macro')) {
      return 0;
    }
  }

  return 0;
};

/**
 * Set range start boundary based on node type and offset.
 */
const setRangeStart = (range: Range, node: Node, offset: number): void => {
  if (node.nodeType === Node.TEXT_NODE) {
    range.setStart(node, offset);
  } else {
    range.setStartBefore(node);
  }
};

/**
 * Set range end boundary based on node type and offset.
 */
const setRangeEnd = (range: Range, node: Node, offset: number): void => {
  if (node.nodeType === Node.TEXT_NODE) {
    range.setEnd(node, offset);
  } else if ((node as HTMLElement).tagName === 'BR') {
    range.setEndBefore(node);
  } else {
    range.setEndAfter(node);
  }
};

/**
 * Build a Range given start and end character offsets (absolute) inside the editor's text.
 * Offsets are measured against extractPlainText (which includes macro span text content).
 * This function must walk the DOM the same way as extractPlainText to match offsets correctly.
 */
export const buildRangeFromOffsets = (
  editor: HTMLElement,
  startOffset: number,
  endOffset: number,
): Range => {
  const range = document.createRange();
  let acc = 0;

  // Walk the same way as extractPlainText: both text nodes AND elements
  const walker = document.createTreeWalker(
    editor,
    NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
    null,
  );

  let node: Node | null;
  let startSet = false;
  let startNode: Node | null = null;
  let startNodeOffset = 0;
  let endNode: Node | null = null;
  let endNodeOffset = 0;
  let prevNode: Node | null = null;

  while ((node = walker.nextNode())) {
    const len = getNodeTextLength(node, acc, prevNode);
    const nodeStart = acc;
    const nodeEnd = acc + len;

    // Check if start offset is in this node
    if (!startSet && startOffset >= nodeStart && startOffset < nodeEnd) {
      startNode = node;
      startNodeOffset = node.nodeType === Node.TEXT_NODE ? Math.max(0, startOffset - nodeStart) : 0;
      startSet = true;
    }

    // Check if end offset is in this node
    if (startSet && endOffset >= nodeStart && endOffset <= nodeEnd) {
      endNode = node;
      endNodeOffset =
        node.nodeType === Node.TEXT_NODE ? Math.max(0, Math.min(endOffset - nodeStart, len)) : 0;

      // Set both start and end of range
      if (startNode) {
        setRangeStart(range, startNode, startNodeOffset);
        setRangeEnd(range, endNode, endNodeOffset);
        return range;
      }
    }

    acc = nodeEnd;
    prevNode = node;
  }

  // Fallback: if we found start but not end, set start and collapse to end
  if (startNode) {
    setRangeStart(range, startNode, startNodeOffset);
    range.selectNodeContents(editor);
    range.collapse(false);
  } else {
    // Complete fallback: couldn't find start, collapse to end
    range.selectNodeContents(editor);
    range.collapse(false);
  }

  return range;
};

/**
 * Wrap the contents of the provided range in a macro span (in-place).
 * `macroText` should include braces, e.g. "{path_params.customerID}".
 */
export const wrapRangeWithMacroSpan = (range: Range, macroText: string, isValid: boolean): void => {
  const span = document.createElement('span');
  span.className = isValid ? 'macro macro-valid' : 'macro macro-invalid';
  span.setAttribute('data-macro', macroText);
  span.setAttribute('data-valid', String(isValid));
  span.textContent = macroText;

  range.deleteContents();
  range.insertNode(span);

  const parent = span.parentNode;
  if (parent) {
    parent.normalize();
  }

  // place caret after inserted span
  const sel = window.getSelection();
  if (sel) {
    const r = document.createRange();
    r.setStartAfter(span);
    r.collapse(true);
    sel.removeAllRanges();
    sel.addRange(r);
  }
};

/**
 * Replace a node (or insert) and place cursor after it.
 * If `anchor` is a node, caret will be placed after it. If `anchor` is null, caret is placed at end.
 */
export const replaceRangeWithTextAndPlaceCursor = (
  editor: HTMLElement,
  anchor: Node | null,
  afterNode: Node | null,
): void => {
  const sel = window.getSelection();
  if (sel) {
    const r = document.createRange();
    if (afterNode) {
      r.setStartAfter(afterNode);
    } else if (anchor) {
      r.setStartAfter(anchor);
    } else {
      r.selectNodeContents(editor);
      r.collapse(false);
    }
    sel.removeAllRanges();
    sel.addRange(r);
  }
};

/**
 * Determines if the cursor is actually inside a macro span (not just adjacent to it).
 * This is more precise than just using closest() because it checks if the cursor
 * is directly within the macro span's content, not just in a parent element.
 *
 * @param range - The selection range to check
 * @returns The macro span element if cursor is inside it, null otherwise
 */
export const findMacroSpanAtCursor = (range: Range): HTMLElement | null => {
  const startContainer = range.startContainer;

  // Case 1: Cursor is in a text node
  if (startContainer.nodeType === Node.TEXT_NODE) {
    const textNode = startContainer as Text;
    const parent = textNode.parentElement;

    // Check if the parent is directly a macro span
    // This is the most common case: typing inside a macro span
    if (parent?.classList.contains('macro')) {
      return parent;
    }

    // If the text node's parent is not a macro, check if we're a sibling to a macro
    // If there's a macro span before this text node, we're typing after it (not inside)
    if (parent) {
      let node: Node | null = textNode.previousSibling;
      while (node) {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const el = node as HTMLElement;
          if (el.classList.contains('macro')) {
            // We're after a macro span, not inside it
            return null;
          }
        }
        node = node.previousSibling;
      }
    }

    // If we get here, the text node is not directly in a macro, and not after one
    // So we're not in a macro span
    return null;
  }

  // Case 2: Cursor is at an element node
  if (startContainer.nodeType === Node.ELEMENT_NODE) {
    const el = startContainer as HTMLElement;

    // If the element itself is a macro span, we're inside it
    if (el.classList.contains('macro')) {
      return el;
    }

    // Check if we're at a position right before a macro span child
    // (meaning cursor is at the boundary, not inside)
    const offset = range.startOffset;
    if (offset < el.childNodes.length) {
      const child = el.childNodes[offset];
      if (child.nodeType === Node.ELEMENT_NODE) {
        const childEl = child as HTMLElement;
        if (childEl.classList.contains('macro')) {
          // Cursor is right before a macro span, not inside it
          return null;
        }
      }
    }

    // Check if we're inside a macro span (but not the macro span itself)
    const macroSpan = el.closest('.macro');
    if (macroSpan && macroSpan !== el) {
      // We're inside a macro span
      return macroSpan as HTMLElement;
    }

    return null;
  }

  return null;
};
